/**
 * pe3d designs: the playEngine Design tab's document (modules/playEngineDesign.js —
 * groups, fields, the tree helpers) laid over a pe3d release instead of a
 * snapshot's globalVariables. Stored per base + company + game + release on the
 * server (controller/pe3d/designStore.js); it holds no values.
 *
 * A field's `path` names what it edits, in the variant as the edited language
 * resolves (usePe3dEditor `view`):
 *
 *   "text.cta"               a config field (the schema's dotted path)
 *   "asset:background"        an asset slot (the playEngine design's asset prefix)
 *   "playable:endType"        a playable setting: storeLinks.ios / storeLinks.android /
 *                             endType / sipPlayable
 *
 * Types, bounds and choices come from the release's schema, so nothing is
 * guessed; a design only chooses grouping, order, labels and the control.
 * Pure: no React, no I/O.
 */

import _ from "lodash";
import { ASSET_FIELD_PREFIX, DESIGN_FORMAT_VERSION, assetKeyFromPath, humanizeKey } from "./playEngineDesign";

export const PLAYABLE_FIELD_PREFIX = "playable:";

/** The playable settings a design row can name, with how they are edited. */
export const PLAYABLE_SETTINGS = {
  "storeLinks.ios": { type: "string", label: "App Store link" },
  "storeLinks.android": { type: "string", label: "Google Play link" },
  endType: { type: "string", label: "End type", options: ["endCard", "directStore", "combined"] },
  sipPlayable: { type: "boolean", label: "Store in playable" },
};

/** The playEngine design type a schema field is edited as. */
function designTypeOf(field) {
  switch (field.kind) {
  case "num":
    return field.int ? "integer" : "number";
  case "bool":
    return "boolean";
  case "color":
    return "color";
  case "str":
  case "enum":
    return "string";
  // vec3, list and table are arrays the pe3d field inputs edit as they are.
  default:
    return "array";
  }
}

/** A schema `{ x, y }` group of two numbers (an `orientedPoint()` half). */
function isPointGroup(field) {
  const fields = (field && field.kind === "group" && field.fields) || {};
  return Object.keys(fields).length === 2 && _.get(fields, "x.kind") === "num" && _.get(fields, "y.kind") === "num";
}

/**
 * A schema `oriented()` / `orientedPoint()` pair (a HUD element's scale or
 * position): a group of exactly `portrait` and `landscape`, leaves of one kind
 * or both `{ x, y }` points. A design edits it as ONE responsive field — a
 * number, or a vec2 like the older games' "Position" — the half the preview's
 * orientation shows (its leaves stay reachable by their own paths).
 */
export function isOrientedGroup(field) {
  if (!field || field.kind !== "group") return false;
  const fields = field.fields || {};
  const { portrait, landscape } = fields;
  if (Object.keys(fields).length !== 2 || !portrait || !landscape) return false;
  if (isPointGroup(portrait) && isPointGroup(landscape)) return true;
  return portrait.kind === landscape.kind && portrait.kind !== "group";
}

/** Every oriented pair of a schema's config: [{ path, field }]. */
function orientedGroups(fields, prefix = "") {
  const out = [];
  for (const [key, field] of Object.entries(fields || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isOrientedGroup(field)) out.push({ path, field });
    else if (field.kind === "group") out.push(...orientedGroups(field.fields, path));
  }
  return out;
}

/** Every leaf of a schema's config: [{ path, field }], groups flattened. */
function configLeaves(fields, prefix = "") {
  const out = [];
  for (const [key, field] of Object.entries(fields || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (field.kind === "group") out.push(...configLeaves(field.fields, path));
    else out.push({ path, field });
  }
  return out;
}

/**
 * What a release offers a design: path → { kind: "config" | "asset" | "playable",
 * type, label, … } — the schema field or slot behind it included.
 */
export function designTargets(schema) {
  const targets = {};
  for (const { path, field } of configLeaves(schema && schema.config)) {
    targets[path] = {
      kind: "config",
      path,
      type: designTypeOf(field),
      label: field.label || humanizeKey(path.slice(path.lastIndexOf(".") + 1)),
      field,
      ...(field.kind === "enum" && { options: field.values }),
    };
  }
  // A pair is one responsive field: bounds and type from its portrait half (a point's from its x).
  for (const { path, field } of orientedGroups(schema && schema.config)) {
    const point = isPointGroup(field.fields.portrait);
    const half = point ? field.fields.portrait.fields.x : field.fields.portrait;
    targets[path] = {
      kind: "config",
      path,
      type: point ? "vec2" : designTypeOf(half),
      responsive: true,
      label: field.label || humanizeKey(path.slice(path.lastIndexOf(".") + 1)),
      field: half,
    };
  }
  for (const [slot, def] of Object.entries((schema && schema.assets) || {})) {
    const path = `${ASSET_FIELD_PREFIX}${slot}`;
    targets[path] = { kind: "asset", path, type: "asset", label: def.label || humanizeKey(slot), slot, def };
  }
  for (const [key, setting] of Object.entries(PLAYABLE_SETTINGS)) {
    const path = `${PLAYABLE_FIELD_PREFIX}${key}`;
    targets[path] = { kind: "playable", path, key, ...setting };
  }
  return targets;
}

/** The design row a target becomes, as the playEngine design writes one. */
export function fieldForTarget(target) {
  const field = { path: target.path, type: target.type, label: target.label };
  if (target.responsive) field.responsive = true;
  if (target.kind === "config") {
    const { min, max, step, help } = target.field;
    // Bounds, but no slider: a bounded number used to come out as a track
    // here while the playEngine design gives every number a box, and the two
    // panels then read as two different editors for the same thing.
    if (typeof min === "number") field.min = min;
    if (typeof max === "number") field.max = max;
    if (typeof step === "number") field.step = step;
    if (help) field.help = help;
  }
  if (target.options) field.options = target.options;
  return field;
}

// Slot kinds, in the order the starter design lists them.
const ASSET_GROUPS = [
  ["image", "Images"], ["texture", "Textures"], ["atlas", "Atlases"], ["spine", "Spine"], ["model", "Models"],
  ["audio", "Sounds"], ["video", "Videos"], ["font", "Fonts"], ["bitmapFont", "Bitmap fonts"], ["sdfFont", "SDF fonts"], ["data", "Data"],
];

/**
 * A first design for a release that has none: its assets grouped by kind, its
 * config as the schema groups it, and its playable settings — everything
 * the release offers, in the order the game declares it.
 */
export function scaffoldPe3dDesign(schema, { title = "" } = {}) {
  const targets = designTargets(schema);
  const groups = [];

  const slots = Object.values(targets).filter((target) => target.kind === "asset");
  const assetGroups = ASSET_GROUPS.map(([kind, label]) => ({
    id: `assets/${kind}`,
    label,
    fields: slots.filter((target) => target.def.kind === kind).map(fieldForTarget),
    groups: [],
  })).filter((group) => group.fields.length);
  if (assetGroups.length) groups.push({ id: "assets", label: "Assets", fields: [], groups: assetGroups });

  function configGroup(fields, prefix, id, label) {
    const own = [];
    const inner = [];
    for (const [key, field] of Object.entries(fields || {})) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (isOrientedGroup(field)) own.push(fieldForTarget(targets[path]));
      else if (field.kind === "group") inner.push(configGroup(field.fields, path, `${id}/${key}`, field.label || humanizeKey(key)));
      else own.push(fieldForTarget(targets[path]));
    }
    return { id, label, path: prefix || undefined, fields: own, groups: inner.filter((group) => group.fields.length || group.groups.length) };
  }
  const config = (schema && schema.config) || {};
  const loose = Object.fromEntries(Object.entries(config).filter(([, field]) => field.kind !== "group"));
  if (Object.keys(loose).length) groups.push(configGroup(loose, "", "general", "General"));
  for (const [key, field] of Object.entries(config)) {
    if (field.kind === "group") {
      const group = configGroup(field.fields, key, key, field.label || humanizeKey(key));
      if (group.fields.length || group.groups.length) groups.push(group);
    }
  }

  groups.push({
    id: "playable",
    label: "Playable",
    fields: Object.values(targets).filter((target) => target.kind === "playable").map(fieldForTarget),
    groups: [],
  });
  return { formatVersion: DESIGN_FORMAT_VERSION, title, groups: groups.map(({ path, ...group }) => (path ? { ...group, path } : group)) };
}

/**
 * The design with each row's missing `type` (and label) filled in from the schema: a game's
 * design.json may name only paths, and the playEngine design drops a row without a type.
 */
export function completeDesign(design, schema) {
  if (!_.isPlainObject(design)) return design;
  const targets = designTargets(schema);
  const walk = (groups) =>
    (Array.isArray(groups) ? groups : []).map((group) => ({
      ...group,
      fields: (group.fields || []).map((field) => {
        // A design stored before pe3d gave up sliders still asks for one;
        // the editors here are the playEngine's, and those have no slider.
        const own = field && field.control === "slider" ? _.omit(field, "control") : field;
        const target = own && targets[own.path];
        if (!target) return own;
        // A typed row still follows the preview when it names a portrait/landscape pair.
        if (own.type) return target.responsive && own.responsive === undefined ? { ...own, responsive: true } : own;
        return { ...fieldForTarget(target), ...own, type: target.type };
      }),
      groups: walk(group.groups),
    }));
  return { ...design, groups: walk(design.groups) };
}

/** The rows a release cannot back (a field or slot its schema has not), as a list of paths. */
export function unusedPaths(design, schema) {
  const targets = designTargets(schema);
  const out = [];
  (function walk(groups) {
    for (const group of groups || []) {
      for (const field of group.fields || []) if (!targets[field.path]) out.push(field.path);
      walk(group.groups);
    }
  })(design && design.groups);
  return out;
}

/** The design with only the rows this release backs; groups left empty go too. */
export function pruneDesignToRelease(design, schema) {
  if (!_.isPlainObject(design)) return design || null;
  const targets = designTargets(schema);
  function walk(groups) {
    return (Array.isArray(groups) ? groups : [])
      .map((group) => ({ ...group, fields: (group.fields || []).filter((field) => targets[field.path]), groups: walk(group.groups) }))
      .filter((group) => group.fields.length || group.groups.length);
  }
  return { ...design, groups: walk(design.groups) };
}

/** What the release offers that the design does not show yet, as rows ready to add. */
export function missingTargets(design, schema) {
  const shown = new Set();
  (function walk(groups) {
    for (const group of groups || []) {
      for (const field of group.fields || []) shown.add(field.path);
      walk(group.groups);
    }
  })(design && design.groups);
  return Object.values(designTargets(schema)).filter((target) => !shown.has(target.path));
}

/** The value a row shows, read from the language's view of the variant. */
export function readDesignValue(target, view, effectiveConfig) {
  if (!target || !view) return undefined;
  if (target.kind === "config") return _.get(effectiveConfig, target.path);
  if (target.kind === "asset") return ((view.assets || {})[target.slot]) || null;
  return _.get(view, target.key);
}

/**
 * The view after a row wrote `value`. Config goes through the release's own
 * computeConfigDelta (the variant keeps only what differs from the defaults),
 * exactly as the Config tab writes it; empty playable values are removed.
 */
export function writeDesignValue(target, view, value, code, schema) {
  const next = _.cloneDeep(view || {});
  if (target.kind === "config") {
    const effective = code.effectiveConfig(schema.config, next.config);
    _.set(effective, target.path, value);
    const delta = code.computeConfigDelta(schema.config, effective);
    if (_.isEmpty(delta)) delete next.config;
    else next.config = delta;
  } else if (target.kind === "asset") {
    const assets = { ...(next.assets || {}) };
    if (value) assets[target.slot] = value;
    else delete assets[target.slot];
    if (_.isEmpty(assets)) delete next.assets;
    else next.assets = assets;
  } else if (value === undefined || value === null || value === "") {
    _.unset(next, target.key);
    if (_.isEmpty(next.storeLinks)) delete next.storeLinks;
  } else _.set(next, target.key, value);
  return next;
}

export { assetKeyFromPath };
